/** Vorschaubilder für Katalog und Möbel-Baukasten (eigener kleiner WebGL-Renderer). */
import * as THREE from "three";
import { makeParts } from "./furniture";
import type { CatalogDef } from "./catalog";

let thumbR: THREE.WebGLRenderer | null = null, thumbScene: THREE.Scene, thumbCam: THREE.PerspectiveCamera;
let failed = false;

function setup(): boolean {
  if (thumbR) return true;
  if (failed) return false;
  try { thumbR = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true }); }
  catch { failed = true; return false; }
  thumbR.outputColorSpace = THREE.SRGBColorSpace; thumbR.toneMapping = THREE.ACESFilmicToneMapping; thumbR.setPixelRatio(1);
  thumbScene = new THREE.Scene();
  thumbScene.add(new THREE.HemisphereLight(0xffffff, 0x8a7d6a, 1.7));
  const dl = new THREE.DirectionalLight(0xffffff, 1.7); dl.position.set(3, 5, 4); thumbScene.add(dl);
  thumbCam = new THREE.PerspectiveCamera(30, 1, 0.01, 100);
  return true;
}

type ModelDef = Pick<CatalogDef, "id" | "w" | "d" | "h" | "c" | "parts">;

/** rendert ein Möbel und liefert das Canvas des Vorschau-Renderers (wird beim nächsten Aufruf überschrieben) */
export function renderModel(def: ModelDef, W: number, H: number, angle = 0.7): HTMLCanvasElement | null {
  if (!setup() || !thumbR) return null;
  thumbR.setSize(W, H, false); thumbCam.aspect = W / H;
  const g = makeParts(def.id, def.w, def.d, def.h, def.c, def.parts ? { w: def.w, d: def.d, h: def.h, c: def.c, parts: def.parts } : null);
  thumbScene.add(g);
  const sph = new THREE.Box3().setFromObject(g).getBoundingSphere(new THREE.Sphere());
  const fov = THREE.MathUtils.degToRad(thumbCam.fov / 2);
  const dist = (Math.max(sph.radius, 0.05) / Math.sin(Math.min(fov, Math.atan(Math.tan(fov) * thumbCam.aspect)))) * 1.04;
  const dir = new THREE.Vector3(Math.sin(angle), 0.62, Math.cos(angle)).normalize();
  thumbCam.position.copy(sph.center).addScaledVector(dir, dist);
  thumbCam.near = dist / 50; thumbCam.far = dist * 4; thumbCam.lookAt(sph.center); thumbCam.updateProjectionMatrix();
  thumbR.render(thumbScene, thumbCam);
  thumbScene.remove(g); g.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
  return thumbR.domElement;
}

const cache = new Map<string, string>();
let queue: [CatalogDef, (src: string) => void][] = [];
let busy = false;

export function forgetThumb(key: string) { cache.delete(key); }
export function forgetCustomThumbs() { for (const k of [...cache.keys()]) if (k.startsWith("cu:")) cache.delete(k); }

/** Vorschaubild anfordern; cb bekommt eine data:-URL (oder wird nie gerufen, wenn WebGL fehlt) */
export function requestThumb(def: CatalogDef, cb: (src: string) => void) {
  const hit = cache.get(def.key);
  if (hit != null) { if (hit) cb(hit); return; }
  // Anfragen eines Durchgangs (= gerade sichtbare Liste) kommen gesammelt vor ältere, Reihenfolge bleibt
  fresh.push([def, cb]);
  if (fresh.length === 1) queueMicrotask(() => { queue = fresh.concat(queue); fresh = []; if (!busy) { busy = true; requestAnimationFrame(step); } });
}
let fresh: [CatalogDef, (src: string) => void][] = [];
function step() {
  const t0 = performance.now();
  while (queue.length && performance.now() - t0 < 14) {
    const [def, cb] = queue.shift()!;
    if (!cache.has(def.key)) {
      try { const cv = renderModel(def, 112, 112); cache.set(def.key, cv ? cv.toDataURL("image/png") : ""); }
      catch { cache.set(def.key, ""); }
    }
    const src = cache.get(def.key); if (src) cb(src);
  }
  if (queue.length) requestAnimationFrame(step); else busy = false;
}
